import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { useI18n } from '../../contexts/I18nContext';
import { Client } from '../../types/client';
import { isAgency } from '../../utils/clientTree';
import { nicFrom, sirenDigits, sirenFrom } from '../../utils/siren';
import SearchableSelect from '../common/SearchableSelect';

export interface CreatedClient {
  name: string;
  siren?: string;
  parent_id?: string;
  nic?: string;
}

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (client: CreatedClient) => void;
}

type Kind = 'client' | 'agency';

/**
 * A new client or a new agency (docs/agences.md on the API side), for
 * SuperAdmin only (docs/droits-acces.md).
 *
 * A client (a head office, or a company without agencies) has a name and,
 * when known, a SIREN — a SIRET is accepted and its SIREN part kept. An
 * agency belongs to a root client and has a NIC, the five digits that
 * complete the root's SIREN into the agency's SIRET — a SIRET is accepted
 * when it starts with the root's SIREN. Status and access are set in the
 * page afterwards.
 */
export const CreateClientModal: React.FC<CreateClientModalProps> = ({ isOpen, onClose, onClientCreated }) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [kind, setKind] = useState<Kind>('client');
  const [clientName, setClientName] = useState('');
  const [sirenInput, setSirenInput] = useState('');
  const [parentId, setParentId] = useState('');
  const [nicInput, setNicInput] = useState('');
  const [roots, setRoots] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [sirenError, setSirenError] = useState<string | null>(null);
  const [parentError, setParentError] = useState<string | null>(null);
  const [nicError, setNicError] = useState<string | null>(null);

  // The root clients an agency may belong to: never another agency.
  useEffect(() => {
    if (!isOpen) return;
    clientService.fetchActiveClients()
      .then((clients) => setRoots(clients.filter((c) => !isAgency(c)).sort((a, b) => a.name.localeCompare(b.name))))
      .catch((err) => { console.error('[CREATE CLIENT] clients failed to load:', err); setRoots([]); });
  }, [isOpen]);

  const parent = useMemo(() => roots.find((c) => c.id === parentId) ?? null, [roots, parentId]);
  const siren = sirenFrom(sirenInput);
  const nic = nicFrom(nicInput, parent?.siren ?? null);

  const validate = (): boolean => {
    const name = clientName.trim();
    let message: string | null = null;
    if (!name) message = t('errors.fieldRequired');
    else if (name.length < 2) message = t('clients.create.nameMin');
    else if (name.length > 256) message = t('clients.create.nameMax');
    setNameError(message);
    if (kind === 'client') {
      const sirenMessage = siren.kind === 'invalid' ? t('clients.sirenDigitsHint') : null;
      setSirenError(sirenMessage);
      return message === null && sirenMessage === null;
    }
    const parentMessage = parent ? null : t('clients.create.parentRequired');
    setParentError(parentMessage);
    const nicMessage = nic.kind === 'invalid' ? t('clients.create.nicDigitsHint')
      : nic.kind === 'mismatch' ? t('clients.create.siretMismatch', { siren: parent?.siren ?? '' })
        : null;
    setNicError(nicMessage);
    return message === null && parentMessage === null && nicMessage === null;
  };

  const reset = () => {
    setKind('client');
    setClientName('');
    setSirenInput('');
    setParentId('');
    setNicInput('');
    setNameError(null);
    setSirenError(null);
    setParentError(null);
    setNicError(null);
  };

  const payload = (): CreatedClient => {
    const name = clientName.trim();
    if (kind === 'agency') {
      return { name, parent_id: parentId, ...(nic.nic ? { nic: nic.nic } : {}) };
    }
    return { name, ...(siren.siren ? { siren: siren.siren } : {}) };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setIsLoading(true);
    try {
      // A plain POST: the generated client model knows neither siren nor parent_id and would drop them.
      const clientData = payload();
      await apiClient.getAxiosInstance().post('/v2/client', clientData);
      // Bust the client cache so the other forms see the new client at once.
      clientService.clearCache();
      onClientCreated(clientData);
      reset();
      onClose();
    } catch (err: any) {
      console.error('[CREATE CLIENT] Error creating client:', err);
      let errorMessage = t('errors.failedToSave');
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        errorMessage = detail.map((d: any) => `${d.loc?.join('.') || 'field'}: ${d.msg}`).join(', ');
      } else if (typeof detail === 'string') {
        if (detail.includes('duplicate key') || detail.includes('already exists')) {
          errorMessage = detail.includes('SIREN') || detail.includes('NIC') ? detail : t('clients.create.duplicate', { name: clientName.trim() });
        } else if (detail.includes('psycopg2')) {
          errorMessage = detail.split('\n')[0];
        } else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const name = clientName.trim();
  const kindButton = (value: Kind, label: string, hint: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={kind === value}
      className={`clc-kind${kind === value ? ' is-active' : ''}`}
      data-testid={`kind-${value}`}
      onClick={() => { setKind(value); setSirenError(null); setParentError(null); setNicError(null); }}
      disabled={isLoading}
    >
      <strong>{label}</strong>
      <span>{hint}</span>
    </button>
  );

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="config-template-modal tpc clc" data-testid="modal-create-client" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('clients.addClient')}</h2>
              <div className="tpd-title-sub"><span>{t('clients.create.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={handleClose} disabled={isLoading} aria-label={t('common.close')}>×</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {error && <div className="error-message" role="alert">{error}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('clients.create.kind')}</h3>
                <p>{t('clients.create.kindIntro')}</p>
              </header>
              <div className="clc-kinds" role="radiogroup" aria-label={t('clients.create.kind')}>
                {kindButton('client', t('clients.create.kindClient'), t('clients.create.kindClientHint'))}
                {kindButton('agency', t('clients.create.kindAgency'), t('clients.create.kindAgencyHint'))}
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('clients.information')}</h3>
                <p>{kind === 'agency' ? t('clients.create.introAgency') : t('clients.create.intro')}</p>
              </header>

              {kind === 'agency' && (
                <div className="cu-field">
                  <label htmlFor="clientParent">{t('clients.create.parentLabel')} *</label>
                  <SearchableSelect
                    id="clientParent"
                    data-testid="field-client-parent"
                    value={parentId}
                    onChange={(next) => { setParentId(next); if (parentError) setParentError(null); setNicError(null); }}
                    options={roots.map((c) => ({ value: c.id, label: c.siren ? `${c.name} · ${c.siren}` : c.name }))}
                    placeholder={t('clients.create.parentPlaceholder')}
                    className={`field-select${parentError ? ' is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {parentError
                    ? <span className="cu-error" data-testid="error-client-parent">{parentError}</span>
                    : parent && !parent.siren
                      ? <span className="cu-hint">{t('clients.create.parentNoSiren')}</span>
                      : <span className="cu-hint">{t('clients.create.parentHint')}</span>}
                </div>
              )}

              <div className="cu-field">
                <label htmlFor="clientName">{kind === 'agency' ? t('clients.create.agencyName') : t('clients.clientName')} *</label>
                <input
                  type="text"
                  id="clientName"
                  data-testid="field-client-name"
                  value={clientName}
                  onChange={(e) => { setClientName(e.target.value); if (nameError) setNameError(null); }}
                  className={`cu-input${nameError ? ' is-invalid' : ''}`}
                  placeholder={kind === 'agency' ? t('clients.create.agencyNamePlaceholder') : t('clients.namePlaceholderDetailed')}
                  disabled={isLoading}
                  autoFocus
                  maxLength={256}
                />
                {nameError ? <span className="cu-error">{nameError}</span> : <span className="cu-hint">{t('clients.create.nameHint')}</span>}
              </div>

              {kind === 'client' ? (
                <div className="cu-field">
                  <label htmlFor="clientSiren">{t('clients.sirenOrSiret')}</label>
                  <input
                    type="text"
                    id="clientSiren"
                    data-testid="field-client-siren"
                    inputMode="numeric"
                    value={sirenInput}
                    onChange={(e) => { setSirenInput(sirenDigits(e.target.value)); if (sirenError) setSirenError(null); }}
                    className={`cu-input mono${sirenError ? ' is-invalid' : ''}`}
                    placeholder={t('clients.detail.sirenPlaceholder')}
                    disabled={isLoading}
                    maxLength={14}
                  />
                  {sirenError
                    ? <span className="cu-error" data-testid="error-client-siren">{sirenError}</span>
                    : siren.kind === 'siret'
                      ? <span className="cu-hint is-ok" data-testid="hint-client-siren">{t('clients.siretOk', { siren: siren.siren ?? '' })}</span>
                      : siren.kind === 'siren'
                        ? <span className="cu-hint is-ok" data-testid="hint-client-siren">{t('clients.detail.sirenOk')}</span>
                        : <span className="cu-hint" data-testid="hint-client-siren">{t('clients.detail.sirenHint')}</span>}
                </div>
              ) : (
                <div className="cu-field">
                  <label htmlFor="clientNic">{t('clients.create.siretOrNic')}</label>
                  <input
                    type="text"
                    id="clientNic"
                    data-testid="field-client-nic"
                    inputMode="numeric"
                    value={nicInput}
                    onChange={(e) => { setNicInput(sirenDigits(e.target.value)); if (nicError) setNicError(null); }}
                    className={`cu-input mono${nicError ? ' is-invalid' : ''}`}
                    placeholder={parent?.siren ? `${parent.siren}00012` : '00012'}
                    disabled={isLoading}
                    maxLength={14}
                  />
                  {nicError
                    ? <span className="cu-error" data-testid="error-client-nic">{nicError}</span>
                    : nic.kind === 'siret'
                      ? <span className="cu-hint is-ok" data-testid="hint-client-nic">{t('clients.create.siretNicOk', { nic: nic.nic ?? '' })}</span>
                      : nic.kind === 'nic'
                        ? <span className="cu-hint is-ok" data-testid="hint-client-nic">{t('clients.create.nicOk', { siret: parent?.siren ? `${parent.siren}${nic.nic}` : '' })}</span>
                        : <span className="cu-hint" data-testid="hint-client-nic">{t('clients.create.nicHint')}</span>}
                </div>
              )}
              <p className="cu-note">{kind === 'agency' ? t('clients.create.noteAgency') : t('clients.create.note')}</p>
            </section>
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary" data-testid="create-client-summary">
              {name ? <strong>{name}</strong> : <span className="tpd-muted">{t('clients.create.summaryEmpty')}</span>}
              {name && kind === 'client' && siren.siren && <span className="mono"> · {siren.siren}</span>}
              {name && kind === 'agency' && parent && <span> · {t('clients.agencies.agencyOf', { name: parent.name })}</span>}
            </div>
            <div className="tpc-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={handleClose} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isLoading}>
                {isLoading ? t('clients.create.creating') : kind === 'agency' ? t('clients.create.submitAgency') : t('clients.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
